import React, { useEffect, useRef } from "react";

const SKIP_TAGS = ["ARTICLE"]; // Tag names to skip typing them 

function AutoTextType({ text, typingSpeed = 5, handleTypingStatus }) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!text || !containerRef.current) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "text/html");
        const nodes = Array.from(doc.body.childNodes);

        handleTypingStatus(true);

        const typeText = (text, parent, onComplete) => {
            let i = 0;
            const span = document.createElement("span");
            parent.appendChild(span);

            const typeChar = () => {
                if (i < text.length) {
                    span.textContent += text[i++];
                    setTimeout(typeChar, typingSpeed);
                } else {
                    onComplete();
                }
            };
            typeChar();
        };

        const typeNode = (node, parent, onComplete) => {
            if (node.nodeType === Node.TEXT_NODE) {
                typeText(node.textContent, parent, onComplete);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                if (SKIP_TAGS.includes(node.tagName)) {
                    parent.appendChild(node.cloneNode(true));
                    onComplete();
                }
                else if (node.tagName === "TABLE") {
                    const table = node.cloneNode(false);
                    parent.appendChild(table);

                    const parts = ["THEAD", "TBODY", "TFOOT"].map(tag =>
                        Array.from(node.getElementsByTagName(tag)).find(Boolean)
                    ).filter(Boolean);

                    let partIndex = 0;

                    const processPart = () => {
                        if (partIndex >= parts.length) return onComplete();

                        const part = parts[partIndex++];
                        const clonedPart = document.createElement(part.tagName);
                        table.appendChild(clonedPart);

                        const rows = Array.from(part.rows);
                        let rowIndex = 0;

                        const processRow = () => {
                            if (rowIndex >= rows.length) {
                                processPart();
                                return;
                            }

                            const row = rows[rowIndex++];
                            const clonedRow = row.cloneNode(false);
                            clonedPart.appendChild(clonedRow);

                            const cells = Array.from(row.cells);
                            let cellIndex = 0;

                            const processCell = () => {
                                if (cellIndex >= cells.length) {
                                    setTimeout(processRow, typingSpeed * 2); // delay between row printing
                                    return;
                                }

                                const cell = cells[cellIndex++];
                                const clonedCell = cell.cloneNode(false);
                                clonedRow.appendChild(clonedCell);

                                const children = Array.from(cell.childNodes);
                                let childIndex = 0;

                                const typeCellContent = () => {
                                    if (childIndex >= children.length) {
                                        processCell();
                                        return;
                                    }
                                    typeNode(children[childIndex++], clonedCell, typeCellContent);
                                };

                                typeCellContent();
                            };

                            processCell();
                        };

                        processRow();
                    };

                    processPart();
                }
                else if (node.tagName === "PRE") {
                    const pre = document.createElement("pre");
                    const code = document.createElement("code");
                    pre.appendChild(code);
                    parent.appendChild(pre);

                    const codeNode = node.querySelector("code");
                    const rawContent = (codeNode && codeNode.textContent) || node.textContent || "";

                    let i = 0;

                    const typeChar = () => {
                        if (i < rawContent.length) {
                            code.textContent += rawContent[i++];
                            setTimeout(typeChar, typingSpeed);
                        } else {
                            onComplete();
                        }
                    };

                    typeChar();
                    return;
                }
                else {
                    const el = node.cloneNode(false);
                    parent.appendChild(el);

                    const children = Array.from(node.childNodes);
                    let i = 0;

                    const typeNextChild = () => {
                        if (i >= children.length) {
                            onComplete();
                        } else {
                            typeNode(children[i++], el, typeNextChild);
                        }
                    };

                    typeNextChild();
                }
            } else {
                onComplete();
            }
        };

        const typeAllNodes = (index = 0) => {
            if (index >= nodes.length) {
                handleTypingStatus(false);
                return;
            }

            typeNode(nodes[index], containerRef.current, () => {
                typeAllNodes(index + 1);
            });
        };

        typeAllNodes();
    }, [text]);

    return <div ref={containerRef} />;
}

export default AutoTextType;
